import { Star, StarHalf } from "lucide-react";
import { cn } from "@/lib/utils";

interface StarRatingProps {
  rating: number;
  size?: "xs" | "sm" | "md" | "lg";
  showCount?: number;
  showValue?: boolean;
  className?: string;
  onRate?: (r: number) => void;
  interactive?: boolean;
}

const sizeMap = {
  xs: "w-3 h-3",
  sm: "w-3.5 h-3.5",
  md: "w-4 h-4",
  lg: "w-5 h-5",
};

const StarRating = ({
  rating,
  size = "sm",
  showCount,
  showValue = false,
  className,
  onRate,
  interactive = false,
}: StarRatingProps) => {
  const safe = Math.max(0, Math.min(5, Number(rating) || 0));
  const sz = sizeMap[size];

  return (
    <span className={cn("inline-flex items-center gap-1", className)}>
      <span className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => {
          const filled = star <= Math.floor(safe);
          const half = !filled && star - 0.5 <= safe;
          return (
            <span key={star} className="relative inline-block">
              <Star
                className={cn(
                  sz,
                  interactive && "cursor-pointer",
                  filled ? "text-yellow-500 fill-yellow-500" : "text-border",
                )}
                onClick={() => interactive && onRate?.(star)}
              />
              {half && (
                <StarHalf
                  className={cn(sz, "absolute inset-0 text-yellow-500 fill-yellow-500 pointer-events-none")}
                />
              )}
            </span>
          );
        })}
      </span>
      {showValue && safe > 0 && (
        <span className="text-xs tabular-nums">{safe.toFixed(1)}</span>
      )}
      {typeof showCount === "number" && (
        <span className="text-xs">({showCount})</span>
      )}
    </span>
  );
};

export default StarRating;
